// src/pages/FundPlanner/components/Portfolios/portfolioReportPositions.js
import { supabase } from "services/supabaseClient";
import { computePositionYields } from "lib/bondMath";
import {
  buildPortfolioPeriodAnalysis,
  buildPortfolioSnapshot,
  getEligibleContractualCallDate,
  normalizeCouponRate as normalizeFundPlannerCouponRate,
} from "pages/FundPlanner/lib/portfolioAnalytics";
import { calculatePurchaseCost, roundToCents } from "pages/FundPlanner/lib/money";
import { resolveReportedRating } from "pages/FundPlanner/lib/portfolioRatings";

/* ────────────────────────────────
   Constants
──────────────────────────────── */
export const FUND_TYPE_LABELS = {
  bond_proceeds: "Bond Proceeds",
  capital_reserve_stabilization: "Capital Reserve / Stabilization",
  debt_service: "Debt Service",
  insurance_risk_reserve: "Insurance / Risk Reserve",
  operating: "Operating",
  program: "Program",
  restricted_revenue: "Restricted Revenue",
  other: "Other",
};

export const SLGS_INVESTMENT_TYPES = new Set([
  "SLGS Time Deposit",
  "SLGS Demand Deposit",
]);

/* ────────────────────────────────
   Basic helpers
──────────────────────────────── */
export function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function toNumberOrZero(value) {
  const n = toNumberOrNull(value);
  return n == null ? 0 : n;
}

export function shouldIncludePositionRow(row, reportMode = "single") {
  const endingAmount = toNumberOrNull(
    row?.current_amount ?? row?.amount ?? row?.par_amount
  );
  const hasZeroEndingAmount =
    endingAmount != null && Math.abs(endingAmount) < 1e-9;

  if (!hasZeroEndingAmount) return true;
  if (reportMode !== "period") return false;

  return Math.abs(toNumberOrZero(row?.net_income)) >= 1e-9;
}

export function normalizeYieldRate(value) {
  const n = toNumberOrNull(value);
  if (n == null) return null;
  return Math.abs(n) > 1 ? n / 100 : n;
}

export function trimToEmpty(value) {
  return String(value ?? "").trim();
}

export function todayIso() {
  const date = new Date();
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function addDaysIso(dateIso, days) {
  const base = parseIsoDate(dateIso);
  if (!base) return "";
  base.setDate(base.getDate() + days);
  return base.toISOString().slice(0, 10);
}

export function latestReportDateIso() {
  return addDaysIso(todayIso(), -1);
}

export function parseIsoDate(value) {
  const s = trimToEmpty(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function compareText(a, b) {
  return trimToEmpty(a).localeCompare(trimToEmpty(b), undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

export function daysBetween(startIso, endIso) {
  const a = parseIsoDate(startIso);
  const b = parseIsoDate(endIso);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

export function normalizeFundType(value) {
  const s = trimToEmpty(value);
  return s || "other";
}

export function getFundTypeLabel(value) {
  const normalized = normalizeFundType(value);
  return FUND_TYPE_LABELS[normalized] || normalized || "Other";
}

export function normalizeInvestmentType(value) {
  const s = trimToEmpty(value);
  return s || "Unknown";
}

export function getPositionDisplayName(position) {
  const investmentType = normalizeInvestmentType(position?.investment_type);
  const description = trimToEmpty(position?.raw_security_description);
  return description.toLowerCase().includes("sweep")
    ? `${investmentType} - Sweep`
    : investmentType;
}

export function isSlgsInvestmentType(value) {
  return SLGS_INVESTMENT_TYPES.has(trimToEmpty(value));
}

export function isDemandLikeInvestmentType(value) {
  const s = trimToEmpty(value).toLowerCase();
  return (
    s === "bank deposit" ||
    s === "cash" ||
    s === "lgip" ||
    s === "money market funds" ||
    s === "money market" ||
    s === "slgs demand deposit" ||
    s === "demand deposit"
  );
}

export function isParValuedInvestmentType(value) {
  return isSlgsInvestmentType(value) || isDemandLikeInvestmentType(value);
}

export function shouldHideDateModeYtm(row, reportMode = "single") {
  if (reportMode === "period" || row?.row_type !== "position") return false;
  if (trimToEmpty(row?.security_master_id)) return false;

  const maturityDays = toNumberOrNull(
    row?.days_to_maturity ?? row?.days_to_effective_maturity
  );

  return maturityDays != null && maturityDays <= 1;
}

export function getDisplayYield(row, reportMode = "single") {
  if (row?.row_type !== "position") return null;

  const endingAmount = toNumberOrNull(
    row?.current_amount ?? row?.amount ?? row?.par_amount
  );
  if (
    !row?.is_pending_settlement &&
    endingAmount != null &&
    Math.abs(endingAmount) < 1e-9
  ) {
    return null;
  }

  if (trimToEmpty(row?.investment_type).toLowerCase() === "slgs time deposit") {
    const couponYield = normalizeFundPlannerCouponRate(row?.coupon);
    if (couponYield != null) return couponYield;
  }

  const ytm = shouldHideDateModeYtm(row, reportMode)
    ? null
    : toNumberOrNull(row?.yield_ytm_mid);
  const ytw = toNumberOrNull(row?.yield_ytw_mid);

  if (ytm == null && ytw == null) return null;
  if (ytm == null) return ytw;
  if (ytw == null) return ytm;
  return Math.min(ytm, ytw);
}

export function isMoneyMarketFundInvestmentType(value) {
  const normalized = trimToEmpty(value).toLowerCase();
  return (
    normalized === "mmf" ||
    normalized === "money market fund" ||
    normalized === "money market funds" ||
    normalized === "money market"
  );
}

export function marketDataPercentToDecimal(row) {
  if (trimToEmpty(row?.units) !== "%") return null;
  const value = toNumberOrNull(row?.value);
  return value == null ? null : value / 100;
}

export function applyMmfMarketYields(rows, yieldByPositionId) {
  return (rows || []).map((row) => {
    const position = row?.position || {};
    if (!isMoneyMarketFundInvestmentType(position.investment_type)) return row;

    const marketYield = yieldByPositionId.get(position.id) ?? null;
    return {
      ...row,
      yield_ytm_mid: marketYield,
      yield_ytm_label: "Effective Yield",
      effective_yield: marketYield,
      yield_ytc_mid: null,
      yield_ytw_mid: null,
      effective_yield_is_override: false,
    };
  });
}

export async function loadMmfMarketYields(positions, endDate) {
  const mmfPositions = (positions || []).filter((position) =>
    isMoneyMarketFundInvestmentType(position?.investment_type)
  );
  const positionsByIdentifier = new Map();

  for (const position of mmfPositions) {
    const identifier = trimToEmpty(position?.cusip_or_id).toUpperCase();
    if (!identifier) continue;
    const bucket = positionsByIdentifier.get(identifier) || [];
    bucket.push(position);
    positionsByIdentifier.set(identifier, bucket);
  }

  const yieldByPositionId = new Map();
  await Promise.all(
    [...positionsByIdentifier.entries()].map(async ([identifier, matchingPositions]) => {
      const { data, error } = await supabase
        .from("market_data")
        .select("date, ticker, value, units")
        .ilike("ticker", `${identifier} %`)
        .lte("date", endDate)
        .order("date", { ascending: false })
        .limit(1);

      if (error) throw error;
      const marketYield = marketDataPercentToDecimal(data?.[0]);
      for (const position of matchingPositions) {
        yieldByPositionId.set(position.id, marketYield);
      }
    })
  );

  return yieldByPositionId;
}

export function isOpenAsOf(position, asOfDate, maturityDate = "") {
  const purchaseDate = trimToEmpty(position?.purchase_date);
  const closedDate = trimToEmpty(position?.closed_date);
  const maturity = trimToEmpty(maturityDate);

  if (purchaseDate && purchaseDate > asOfDate) return false;
  if (closedDate && closedDate <= asOfDate) return false;
  if (maturity && maturity <= asOfDate) return false;
  return true;
}

/* ────────────────────────────────
   Resolved position field helpers
──────────────────────────────── */
export function getResolvedIssuerName(position, security) {
  if (isSlgsInvestmentType(position?.investment_type)) {
    return "US Treasury SLGS";
  }
  return (
    trimToEmpty(position?.raw_issuer_name) ||
    trimToEmpty(security?.issuer_name) ||
    "—"
  );
}

export function getResolvedSecurityType(position, security) {
  if (isSlgsInvestmentType(position?.investment_type)) {
    return "SLGS";
  }
  return (
    trimToEmpty(security?.security_type) ||
    trimToEmpty(position?.raw_security_type) ||
    normalizeInvestmentType(position?.investment_type)
  );
}

export function getResolvedSector(position, security) {
  if (isSlgsInvestmentType(position?.investment_type)) {
    return "Sovereign";
  }
  return trimToEmpty(security?.sector) || "—";
}

export function getResolvedAssetClass(position, security) {
  if (isSlgsInvestmentType(position?.investment_type)) {
    return "FixedIncome";
  }
  return trimToEmpty(security?.asset_class) || "—";
}

export function getResolvedSubsector(position, security) {
  if (isSlgsInvestmentType(position?.investment_type)) {
    return "";
  }
  return trimToEmpty(security?.subsector) || "";
}

export function getResolvedRatingRollup(
  position,
  security,
  positionValuation,
  securityValuation
) {
  if (isSlgsInvestmentType(position?.investment_type)) {
    return "AAA";
  }

  const rating = resolveReportedRating({
    investment_type: position?.investment_type,
    security_type: security?.security_type,
    insured_flag: security?.insured_flag,
    rating_rollup: security?.id
      ? securityValuation?.rating_rollup
      : positionValuation?.rating_rollup,
    rating_moody: securityValuation?.rating_moody,
    rating_sp: securityValuation?.rating_sp,
    rating_fitch: securityValuation?.rating_fitch,
  }).normalizedRating;

  return rating === "NR" ? "—" : rating;
}

export function getStaticCoupon(position, security) {
  return (
    toNumberOrNull(position?.raw_coupon) ??
    toNumberOrNull(security?.coupon) ??
    null
  );
}

export function getStaticMaturityDate(position, security) {
  return (
    trimToEmpty(position?.raw_maturity_date) ||
    trimToEmpty(security?.maturity_date) ||
    ""
  );
}

export function getEffectiveMaturityDate(position, security, valuation, asOfDate) {
  const investmentType = normalizeInvestmentType(position?.investment_type);
  const maturityDate = getStaticMaturityDate(position, security);
  const nextCallDate = getEligibleContractualCallDate(
    security,
    asOfDate,
    maturityDate
  );

  if (investmentType.toLowerCase() === "cash") {
    return asOfDate;
  }

  if (isDemandLikeInvestmentType(investmentType)) {
    const base = parseIsoDate(asOfDate);
    if (!base) return asOfDate;
    const plusOne = new Date(base.getTime() + 86400000);
    return plusOne.toISOString().slice(0, 10);
  }

  const isCallable = Boolean(security?.is_callable);
  if (
    isCallable &&
    nextCallDate &&
    maturityDate &&
    nextCallDate <= maturityDate
  ) {
    return nextCallDate;
  }

  return maturityDate || "";
}

/* ────────────────────────────────
   Valuation helpers
──────────────────────────────── */
export function buildLatestValuationMap(rows) {
  const map = new Map();

  for (const row of Array.isArray(rows) ? rows : []) {
    const key = row?.security_master_id;
    if (!key || map.has(key)) continue;
    map.set(key, row);
  }

  return map;
}

/* ────────────────────────────────
   Position row builder
──────────────────────────────── */
export function buildResolvedPositionRow(
  position,
  fund,
  portfolio,
  security,
  valuation,
  asOfDate
) {
  const investmentType = position?.investment_type;
  const isSlgs = isSlgsInvestmentType(investmentType);
  const isParValued = isParValuedInvestmentType(investmentType);
  const settlementDate = trimToEmpty(
    position?.settlement_date || position?.purchase_date
  );
  const isPendingSettlement = Boolean(
    settlementDate && asOfDate && settlementDate > asOfDate
  );

  const parAmount = toNumberOrNull(position?.par_amount);
  const purchasePrice =
    toNumberOrNull(position?.purchase_price) ?? (isParValued ? 100 : null);
  const priceMid = isParValued ? 100 : toNumberOrNull(valuation?.price_mid);
  const coupon = getStaticCoupon(position, security);
  const maturityDate = getStaticMaturityDate(position, security);
  const nextCallDate = isSlgs
    ? ""
    : getEligibleContractualCallDate(security, asOfDate, maturityDate);
  const effectiveMaturityDate = getEffectiveMaturityDate(
    position,
    security,
    valuation,
    asOfDate
  );

  const originalCost =
    parAmount != null && purchasePrice != null
      ? roundToCents(position?.original_cost) ??
        calculatePurchaseCost(parAmount, purchasePrice)
      : null;

  const marketValue =
    parAmount != null && priceMid != null ? (parAmount * priceMid) / 100 : null;

  const unrealizedGainLoss =
    marketValue != null && originalCost != null
      ? marketValue - originalCost
      : null;

  const daysToEffectiveMaturity = daysBetween(asOfDate, effectiveMaturityDate);
  const yearsToEffectiveMaturity =
    daysToEffectiveMaturity == null ? null : daysToEffectiveMaturity / 365.25;

  let computedYtm = null;
  let computedYtc = null;
  let computedYtw = null;

  if (!isSlgs && !isParValued) {
    const computed = computePositionYields({
      purchasePrice,
      settlementDate: trimToEmpty(position?.settlement_date) || "",
      couponType: trimToEmpty(security?.coupon_type),
      couponRate: normalizeFundPlannerCouponRate(coupon),
      maturityDate,
      paymentFrequency: security?.payment_frequency,
      dayCountBasis: trimToEmpty(security?.day_count_basis) || "30/360",
      isCallable: Boolean(security?.is_callable),
      nextCallDate,
    });

    computedYtm = computed.ytm;
    computedYtc = computed.ytc;
    computedYtw = computed.ytw;
  }

  return {
    id: `position:${position.id}`,
    row_type: "position",
    key: `position:${position.id}`,
    depth: 3,
    is_expandable: false,

    portfolio_id: portfolio?.id || "",
    portfolio_name: portfolio?.name || "(Unassigned Portfolio Group)",
    fund_id: fund?.id || "",
    fund_name: fund?.name || "(Untitled Fund)",
    fund_type: normalizeFundType(fund?.fund_type),
    fund_type_label: getFundTypeLabel(fund?.fund_type),

    position_id: position?.id || "",
    is_pending_settlement: isPendingSettlement,
    settlement_status: isPendingSettlement ? "Pending Settlement" : "Settled",
    security_master_id: security?.id || "",
    is_slgs: isSlgs,

    investment_type: normalizeInvestmentType(position?.investment_type),
    security_type: getResolvedSecurityType(position, security),
    insured_flag: Boolean(security?.insured_flag),
    asset_class: getResolvedAssetClass(position, security),
    sector: getResolvedSector(position, security),
    subsector: getResolvedSubsector(position, security),

    cusip_or_id: trimToEmpty(position?.cusip_or_id) || "—",
    issuer_name: getResolvedIssuerName(position, security),
    coupon,
    coupon_type: trimToEmpty(security?.coupon_type),
    payment_frequency: security?.payment_frequency || "",
    day_count_basis: trimToEmpty(security?.day_count_basis) || "",
    issue_date: trimToEmpty(security?.issue_date) || "",
    dated_date: trimToEmpty(security?.dated_date) || "",
    first_interest_payment_date:
      trimToEmpty(security?.first_interest_payment_date) || "",
    is_callable: Boolean(security?.is_callable),
    maturity_date: maturityDate,
    first_call_date: trimToEmpty(security?.display_first_call_date) || "",
    next_call_date: nextCallDate,
    effective_maturity_date: effectiveMaturityDate,
    settlement_date: trimToEmpty(position?.settlement_date) || "",
    purchase_date: trimToEmpty(position?.purchase_date) || "",
    closed_date: trimToEmpty(position?.closed_date) || "",

    par_amount: parAmount,
    purchase_price: purchasePrice,
    original_cost: originalCost,
    price_mid: priceMid,
    market_value: marketValue,
    book_value: null,
    unrealized_gain_loss: unrealizedGainLoss,

    yield_ytm_mid: computedYtm,
    yield_ytm_label: "YTM",
    effective_yield: null,
    yield_ytc_mid: computedYtc,
    yield_ytw_mid: computedYtw,
    rating_rollup: getResolvedRatingRollup(position, security, null, valuation),
    rating_moody: trimToEmpty(valuation?.rating_moody),
    rating_sp: trimToEmpty(valuation?.rating_sp),
    rating_fitch: trimToEmpty(valuation?.rating_fitch),

    days_to_effective_maturity: daysToEffectiveMaturity,
    years_to_effective_maturity: yearsToEffectiveMaturity,

    display_name: getPositionDisplayName(position),
  };
}

function calculateOriginalCostForDisplay(position, parAmount) {
  const explicit = toNumberOrNull(position?.original_cost);
  if (explicit != null) return roundToCents(explicit);

  const par = parAmount ?? toNumberOrNull(position?.par_amount);
  const price = toNumberOrNull(position?.purchase_price);
  if (par == null || price == null) return null;
  return calculatePurchaseCost(par, price);
}

export function buildResolvedPositionRowFromAnalytics(row) {
  const position = row?.position || {};
  const fund = row?.fund || {};
  const portfolio = row?.portfolio || {};
  const security = row?.security || {};
  const securityValuation = row?.securityValuation || {};
  const positionRatingValuation = row?.latest_rating_valuation || {};
  const principalAmount =
    row?.current_amount ?? row?.amount ?? row?.par_amount ?? null;
  const principalOriginalCost =
    row?.original_cost ??
    calculateOriginalCostForDisplay(position, row?.par_amount);
  const principalBookValue = row?.book_value ?? null;

  return {
    id: `position:${position.id}`,
    row_type: "position",
    key: `position:${position.id}`,
    depth: 3,
    is_expandable: false,

    portfolio_id: portfolio?.id || "",
    portfolio_name: portfolio?.name || "(Unassigned Portfolio Group)",
    fund_id: fund?.id || "",
    fund_name: fund?.name || "(Untitled Fund)",
    fund_type: normalizeFundType(fund?.fund_type),
    fund_type_label: getFundTypeLabel(fund?.fund_type),

    position_id: position?.id || "",
    is_pending_settlement: Boolean(row?.is_pending_settlement),
    settlement_status: row?.is_pending_settlement ? "Pending Settlement" : "Settled",
    security_master_id: security?.id || "",
    is_slgs: isSlgsInvestmentType(position?.investment_type),
    security_category: row?.category || "",

    investment_type: normalizeInvestmentType(position?.investment_type),
    security_type: getResolvedSecurityType(position, security),
    insured_flag: Boolean(security?.insured_flag),
    asset_class: getResolvedAssetClass(position, security),
    sector: getResolvedSector(position, security),
    subsector: getResolvedSubsector(position, security),

    cusip_or_id: trimToEmpty(position?.cusip_or_id) || "—",
    issuer_name: getResolvedIssuerName(position, security),
    coupon: row?.coupon ?? null,
    coupon_type: trimToEmpty(security?.coupon_type),
    payment_frequency: security?.payment_frequency || "",
    day_count_basis: trimToEmpty(security?.day_count_basis) || "",
    issue_date: trimToEmpty(security?.issue_date) || "",
    dated_date: trimToEmpty(security?.dated_date) || "",
    first_interest_payment_date:
      trimToEmpty(security?.first_interest_payment_date) || "",
    is_callable: Boolean(security?.is_callable),
    maturity_date: row?.maturity_date || "",
    first_call_date: trimToEmpty(security?.display_first_call_date) || "",
    next_call_date: row?.next_call_date || "",
    effective_maturity_date: row?.effective_maturity_date || "",
    settlement_date: trimToEmpty(position?.settlement_date) || "",
    purchase_date: trimToEmpty(position?.purchase_date) || "",
    closed_date: trimToEmpty(position?.closed_date) || "",

    principal_par_amount: row?.par_amount ?? null,
    principal_amount: principalAmount,
    principal_original_cost: principalOriginalCost,
    principal_book_value: principalBookValue,
    par_amount: row?.par_amount ?? null,
    amount: principalAmount,
    current_amount: principalAmount,
    purchase_price: toNumberOrNull(position?.purchase_price),
    original_cost: principalOriginalCost,
    price_mid: row?.price_mid ?? null,
    market_value: row?.market_value ?? null,
    accrued_interest: row?.accrued_interest ?? null,
    market_value_plus_accrued: row?.market_value_plus_accrued ?? null,
    book_value: principalBookValue,
    unrealized_gain_loss: row?.unrealized_gain_loss ?? null,
    net_income: row?.net_income ?? null,

    yield_ytm_mid: row?.yield_ytm_mid ?? null,
    yield_ytm_label: row?.yield_ytm_label || "YTM",
    effective_yield: row?.effective_yield ?? null,
    yield_ytc_mid: row?.yield_ytc_mid ?? null,
    yield_ytw_mid: row?.yield_ytw_mid ?? null,
    rating_rollup: getResolvedRatingRollup(
      position,
      security,
      positionRatingValuation,
      securityValuation
    ),
    rating_moody: trimToEmpty(securityValuation?.rating_moody),
    rating_sp: trimToEmpty(securityValuation?.rating_sp),
    rating_fitch: trimToEmpty(securityValuation?.rating_fitch),

    days_to_maturity: row?.days_to_maturity ?? null,
    days_to_call: row?.days_to_call ?? null,
    days_to_effective_maturity: row?.days_to_effective_maturity ?? null,
    years_to_effective_maturity: row?.years_to_effective_maturity ?? null,

    display_name: getPositionDisplayName(position),
  };
}

export function buildPeriodPositionRows({
  endingRows = [],
  positionSummaries = [],
  positions = [],
  funds = [],
  portfolios = [],
  securities = [],
  asOfDate = "",
} = {}) {
  const summaryByPositionId = new Map(
    positionSummaries.map((summary) => [summary.positionId, summary])
  );
  const endingPositionIds = new Set(
    endingRows.map((row) => row?.position?.id).filter(Boolean)
  );
  const fundById = new Map(funds.map((fund) => [fund.id, fund]));
  const portfolioById = new Map(portfolios.map((portfolio) => [portfolio.id, portfolio]));
  const securityByCusip = new Map(
    securities.map((security) => [trimToEmpty(security.cusip).toUpperCase(), security])
  );

  const resolvedEndingRows = endingRows.map((row) =>
    buildResolvedPositionRowFromAnalytics({
      ...row,
      net_income: row?.is_pending_settlement
        ? 0
        : summaryByPositionId.get(row?.position?.id)?.netIncome ?? 0,
    })
  );

  const activityOnlyRows = positions
    .filter((position) => !endingPositionIds.has(position.id))
    .filter((position) => toNumberOrZero(summaryByPositionId.get(position.id)?.netIncome) !== 0)
    .map((position) => {
      const fund = fundById.get(position.fund_id) || null;
      const portfolio = fund ? portfolioById.get(fund.portfolio_id) || null : null;
      const security =
        securityByCusip.get(trimToEmpty(position.cusip_or_id).toUpperCase()) || null;
      const row = buildResolvedPositionRow(
        position,
        fund,
        portfolio,
        security,
        null,
        asOfDate
      );

      return {
        ...row,
        // Retain realized period activity in the Positions report without
        // treating the closed position as an ending portfolio holding.
        is_activity_only: true,
        par_amount: 0,
        amount: 0,
        current_amount: 0,
        original_cost: 0,
        price_mid: null,
        market_value: 0,
        accrued_interest: 0,
        market_value_plus_accrued: 0,
        book_value: 0,
        unrealized_gain_loss: 0,
        yield_ytm_mid: null,
        effective_yield: null,
        yield_ytc_mid: null,
        yield_ytw_mid: null,
        days_to_maturity: null,
        days_to_call: null,
        days_to_effective_maturity: null,
        years_to_effective_maturity: null,
        net_income: summaryByPositionId.get(position.id).netIncome,
      };
    });

  return [...resolvedEndingRows, ...activityOnlyRows].filter((row) =>
    shouldIncludePositionRow(row, "period")
  );
}

/* ────────────────────────────────
   Metrics / hierarchy helpers
──────────────────────────────── */
export function buildMetrics(rows) {
  let positionCount = 0;
  let pendingSettlementCount = 0;
  let totalPar = 0;
  let totalOriginalCost = 0;
  let totalMarketValue = 0;
  let totalBookValue = 0;
  let totalAccruedInterest = 0;
  let totalMarketValuePlusAccrued = 0;
  let totalUnrealizedGainLoss = 0;
  let totalNetIncome = 0;
  let couponWeightedSum = 0;
  let maturityWeightedDaysSum = 0;
  let maturityWeight = 0;

  for (const row of rows) {
    if (row.is_pending_settlement) {
      pendingSettlementCount += 1;
    } else {
      positionCount += 1;
    }

    const par = toNumberOrZero(row.current_amount ?? row.amount ?? row.par_amount);
    const oc = toNumberOrZero(row.original_cost);
    const mv = toNumberOrZero(row.market_value);
    const bv = toNumberOrZero(row.book_value);
    const ai = toNumberOrZero(row.accrued_interest);
    const mvai = toNumberOrZero(row.market_value_plus_accrued);
    const ugl = toNumberOrZero(row.unrealized_gain_loss);
    const netIncome = toNumberOrZero(row.net_income);

    totalPar += par;
    totalOriginalCost += oc;
    totalMarketValue += mv;
    totalBookValue += bv;
    totalAccruedInterest += ai;
    totalMarketValuePlusAccrued += mvai;
    totalUnrealizedGainLoss += ugl;
    totalNetIncome += netIncome;

    if (row.coupon != null && par > 0) {
      couponWeightedSum += row.coupon * par;
    }

    if (row.days_to_effective_maturity != null && par > 0) {
      maturityWeightedDaysSum += row.days_to_effective_maturity * par;
      maturityWeight += par;
    }
  }

  return {
    positionCount,
    pendingSettlementCount,
    totalPar,
    totalOriginalCost,
    totalMarketValue,
    totalBookValue,
    totalAccruedInterest,
    totalMarketValuePlusAccrued,
    totalUnrealizedGainLoss,
    totalNetIncome,
    weightedAvgCoupon: totalPar > 0 ? couponWeightedSum / totalPar : null,
    weightedAvgMaturityDays:
      maturityWeight > 0 ? maturityWeightedDaysSum / maturityWeight : null,
  };
}

export function sortPositionRows(rows) {
  return [...rows].sort((a, b) => {
    const typeCmp = compareText(a.investment_type, b.investment_type);
    if (typeCmp !== 0) return typeCmp;

    const settleCmp = compareText(a.settlement_date, b.settlement_date);
    if (settleCmp !== 0) return settleCmp;

    return compareText(a.cusip_or_id, b.cusip_or_id);
  });
}

export function buildHoldingsHierarchy(positionRows) {
  const portfolioMap = new Map();

  for (const row of sortPositionRows(positionRows)) {
    const portfolioKey = row.portfolio_id || "__unassigned_portfolio__";
    const fundTypeKey = row.fund_type || "other";
    const fundKey = row.fund_id || "__unassigned_fund__";

    if (!portfolioMap.has(portfolioKey)) {
      portfolioMap.set(portfolioKey, {
        row_type: "portfolio",
        key: `portfolio:${portfolioKey}`,
        id: `portfolio:${portfolioKey}`,
        label: row.portfolio_name,
        portfolio_id: row.portfolio_id,
        rows: [],
        fundTypes: new Map(),
      });
    }

    const portfolioNode = portfolioMap.get(portfolioKey);
    portfolioNode.rows.push(row);

    if (!portfolioNode.fundTypes.has(fundTypeKey)) {
      portfolioNode.fundTypes.set(fundTypeKey, {
        row_type: "fund_type",
        key: `fundType:${portfolioKey}:${fundTypeKey}`,
        id: `fundType:${portfolioKey}:${fundTypeKey}`,
        label: getFundTypeLabel(fundTypeKey),
        raw_value: fundTypeKey,
        portfolio_id: row.portfolio_id,
        rows: [],
        funds: new Map(),
      });
    }

    const fundTypeNode = portfolioNode.fundTypes.get(fundTypeKey);
    fundTypeNode.rows.push(row);

    if (!fundTypeNode.funds.has(fundKey)) {
      fundTypeNode.funds.set(fundKey, {
        row_type: "fund",
        key: `fund:${portfolioKey}:${fundTypeKey}:${fundKey}`,
        id: `fund:${portfolioKey}:${fundTypeKey}:${fundKey}`,
        label: row.fund_name,
        fund_id: row.fund_id,
        rows: [],
      });
    }

    const fundNode = fundTypeNode.funds.get(fundKey);
    fundNode.rows.push(row);
  }

  return [...portfolioMap.values()]
    .sort((a, b) => compareText(a.label, b.label))
    .map((portfolioNode) => {
      const fundTypes = [...portfolioNode.fundTypes.values()]
        .sort((a, b) => compareText(a.label, b.label))
        .map((fundTypeNode) => {
          const funds = [...fundTypeNode.funds.values()]
            .sort((a, b) => compareText(a.label, b.label))
            .map((fundNode) => ({
              ...fundNode,
              depth: 2,
              is_expandable: true,
              metrics: buildMetrics(fundNode.rows),
              children: sortPositionRows(fundNode.rows).map((row) => ({
                ...row,
                depth: 3,
                is_expandable: false,
              })),
            }));

          return {
            ...fundTypeNode,
            depth: 1,
            is_expandable: true,
            metrics: buildMetrics(fundTypeNode.rows),
            children: funds,
          };
        });

      return {
        ...portfolioNode,
        depth: 0,
        is_expandable: true,
        metrics: buildMetrics(portfolioNode.rows),
        children: fundTypes,
      };
    });
}

export function flattenHierarchy(nodes, expandedKeys) {
  const rows = [];

  function visit(node) {
    rows.push(node);
    if (!node.children?.length) return;
    if (!expandedKeys.has(node.key)) return;
    for (const child of node.children) visit(child);
  }

  for (const node of nodes) visit(node);
  return rows;
}

export function getPositionRowsInHierarchyOrder(nodes) {
  const rows = [];

  function visit(node) {
    if (node.row_type === "position") rows.push(node);
    for (const child of node.children || []) visit(child);
  }

  for (const node of nodes || []) visit(node);
  return rows;
}

/* ────────────────────────────────
   Scope label helper
──────────────────────────────── */
export function scopeLabel(reportScope, includedPortfolioNames) {
  if (!reportScope) return "No scope";
  if (reportScope.includeAllPortfolioGroups) return "All Portfolio Groups";

  const names = includedPortfolioNames.filter(Boolean);
  if (!names.length) return "No Portfolio Group selected";
  if (names.length <= 3) return names.join(", ");
  return `${names.length} Portfolio Groups selected`;
}

/* ────────────────────────────────
   Shared loader
──────────────────────────────── */
export async function loadPortfolioReportPositions({
  orgId,
  asOfDate,
  includeAllPortfolioGroups,
  includedPortfolioIds,
  includedFundIds = [],
  startDate = "",
  endDate = "",
  reportMode = "single",
}) {
  const isPeriodMode = reportMode === "period";
  const effectiveAsOfDate = isPeriodMode ? endDate || asOfDate : asOfDate;
  const effectiveStartDate = isPeriodMode ? startDate || effectiveAsOfDate : "";

  if (!orgId) {
    return {
      portfolios: [],
      funds: [],
      positions: [],
      rows: [],
      periodAnalysis: null,
    };
  }

  const hasDirectFundScope = includedFundIds.length > 0;

  if (!includeAllPortfolioGroups && !includedPortfolioIds.length && !hasDirectFundScope) {
    return {
      portfolios: [],
      funds: [],
      positions: [],
      rows: [],
      periodAnalysis: null,
    };
  }

  let portfolioList = [];
  let portfolioIdByFundId = new Map();
  let fundQuery = supabase
    .from("fp_investment_funds")
    .select("id, name, fund_type, portfolio_id")
    .eq("org_id", orgId);

  if (hasDirectFundScope) {
    fundQuery = fundQuery.in("id", includedFundIds);
  } else {
    let portfolioQuery = supabase
      .from("fp_portfolios")
      .select("id, name")
      .eq("org_id", orgId);

    if (!includeAllPortfolioGroups) {
      portfolioQuery = portfolioQuery.in("id", includedPortfolioIds);
    }

    const { data: portfolios, error: portfoliosError } = await portfolioQuery;
    if (portfoliosError) throw portfoliosError;
    portfolioList = Array.isArray(portfolios) ? portfolios : [];
    if (!portfolioList.length) {
      return { portfolios: [], funds: [], positions: [], rows: [], periodAnalysis: null };
    }

    const selectedPortfolioIds = portfolioList.map((p) => p.id);
    const { data: memberships, error: membershipsError } = await supabase
      .from("fp_investment_fund_portfolios")
      .select("fund_id, portfolio_id")
      .in("portfolio_id", selectedPortfolioIds);

    if (membershipsError) throw membershipsError;

    const fundIds = [];
    for (const membership of memberships || []) {
      if (!portfolioIdByFundId.has(membership.fund_id)) {
        fundIds.push(membership.fund_id);
        portfolioIdByFundId.set(membership.fund_id, membership.portfolio_id);
      }
    }

    if (!fundIds.length) {
      return { portfolios: portfolioList, funds: [], positions: [], rows: [], periodAnalysis: null };
    }
    fundQuery = fundQuery.in("id", fundIds);
  }

  const { data: funds, error: fundsError } = await fundQuery;

  if (fundsError) throw fundsError;

  const fundList = (Array.isArray(funds) ? funds : []).map((fund) => ({
    ...fund,
    portfolio_id: portfolioIdByFundId.get(fund.id) || fund.portfolio_id || "",
  }));
  if (!fundList.length) {
    return {
      portfolios: portfolioList,
      funds: [],
      positions: [],
      rows: [],
      periodAnalysis: null,
    };
  }

  const fundIds = fundList.map((f) => f.id);

  const { data: positions, error: positionsError } = await supabase
    .from("fp_positions")
    .select(`
      id,
      fund_id,
      cusip_or_id,
      purchase_date,
      settlement_date,
      closed_date,
      par_amount,
      current_face,
      units,
      purchase_price,
      original_cost,
      book_value_input,
      market_value_input,
      raw_issuer_name,
      raw_security_description,
      raw_coupon,
      raw_maturity_date,
      day_count_basis,
      interest_frequency,
      raw_security_type,
      investment_type
    `)
    .eq("org_id", orgId)
    .in("fund_id", fundIds)
    .lte("purchase_date", effectiveAsOfDate);

  if (positionsError) throw positionsError;

  const positionList = Array.isArray(positions) ? positions : [];
  const positionIds = positionList.map((p) => p.id).filter(Boolean);

  let activityList = [];
  let positionValuationList = [];

  if (positionIds.length) {
    const [activityRes, valuationRes] = await Promise.all([
      supabase
        .from("fp_position_activity")
        .select("*")
        .eq("org_id", orgId)
        .in("position_id", positionIds)
        // Load the next-day boundary so linked month-end interest
        // reinvestments can be recognized at the prior EOD snapshot. The
        // snapshot state logic still excludes unrelated next-day activity.
        .lte("activity_date", addDaysIso(effectiveAsOfDate, 1))
        .order("activity_date", { ascending: true }),
      supabase
        .from("fp_position_valuations")
        .select("*")
        .eq("org_id", orgId)
        .in("position_id", positionIds)
        .lte("valuation_date", effectiveAsOfDate)
        .order("valuation_date", { ascending: false })
        .order("created_at", { ascending: false }),
    ]);

    if (activityRes.error) throw activityRes.error;
    if (valuationRes.error) throw valuationRes.error;

    activityList = Array.isArray(activityRes.data) ? activityRes.data : [];
    positionValuationList = Array.isArray(valuationRes.data)
      ? valuationRes.data
      : [];
  }

  const marketableCusips = [
    ...new Set(
      positionList
        .filter((p) => !isSlgsInvestmentType(p.investment_type))
        .map((p) => trimToEmpty(p.cusip_or_id))
        .filter(Boolean)
    ),
  ];

  let securityList = [];
  if (marketableCusips.length) {
    const { data: securities, error: securitiesError } = await supabase
      .from("fp_security_master")
      .select(`
        id,
        cusip,
        issuer_name,
        security_type,
        asset_class,
        sector,
        subsector,
        coupon,
        coupon_type,
        issue_date,
        dated_date,
        maturity_date,
        first_interest_payment_date,
        display_first_call_date:first_call_date,
        is_callable,
        insured_flag,
        payment_frequency,
        day_count_basis
      `)
      .in("cusip", marketableCusips);

    if (securitiesError) throw securitiesError;
    securityList = Array.isArray(securities) ? securities : [];
  }

  const securityByCusip = new Map(
    securityList.map((s) => [trimToEmpty(s.cusip), s])
  );

  const openPositions = positionList.filter((p) => {
    const security = securityByCusip.get(trimToEmpty(p.cusip_or_id)) || null;
    const maturityDate =
      trimToEmpty(p?.raw_maturity_date) || trimToEmpty(security?.maturity_date);

    return isOpenAsOf(p, effectiveAsOfDate, maturityDate);
  });

  const positionsForAnalytics = isPeriodMode ? positionList : openPositions;

  if (!positionsForAnalytics.length) {
    return {
      portfolios: portfolioList,
      funds: fundList,
      positions: [],
      rows: [],
      periodAnalysis: null,
    };
  }

  const securityIds = securityList.map((s) => s.id);
  let securityValuationList = [];

  if (securityIds.length) {
    const { data: valuations, error: valuationsError } = await supabase
      .from("fp_security_master_valuations")
      .select(`
        security_master_id,
        valuation_date,
        next_call_date,
        rating_rollup,
        rating_moody,
        rating_sp,
        rating_fitch,
        yield_ytm_mid,
        yield_ytc_mid,
        price_mid
      `)
      .in("security_master_id", securityIds)
      .lte("valuation_date", effectiveAsOfDate)
      .order("valuation_date", { ascending: false });

    if (valuationsError) throw valuationsError;
    securityValuationList = Array.isArray(valuations) ? valuations : [];
  }

  if (isPeriodMode) {
    const periodAnalysis = buildPortfolioPeriodAnalysis({
      startDate: effectiveStartDate,
      endDate: effectiveAsOfDate,
      portfolios: portfolioList,
      funds: fundList,
      positions: positionsForAnalytics,
      activities: activityList,
      valuations: positionValuationList,
      securities: securityList,
      securityValuations: securityValuationList,
    });
    const [mmfYieldByPositionId, comparisonMmfYieldByPositionId] = await Promise.all([
      loadMmfMarketYields(positionsForAnalytics, effectiveAsOfDate),
      periodAnalysis.comparisonPeriod
        ? loadMmfMarketYields(
            positionsForAnalytics,
            periodAnalysis.comparisonPeriod.endDate
          )
        : Promise.resolve(new Map()),
    ]);
    const endingRows = applyMmfMarketYields(
      periodAnalysis.ending.rows,
      mmfYieldByPositionId
    );
    const comparisonRows = applyMmfMarketYields(
      periodAnalysis.comparison?.rows || [],
      comparisonMmfYieldByPositionId
    );
    const periodPositionRows = buildPeriodPositionRows({
      endingRows,
      positionSummaries: periodAnalysis.positionSummaries,
      positions: positionsForAnalytics,
      funds: fundList,
      portfolios: portfolioList,
      securities: securityList,
      asOfDate: effectiveAsOfDate,
    });

    return {
      portfolios: portfolioList,
      funds: fundList,
      positions: positionsForAnalytics,
      rows: periodPositionRows,
      periodAnalysis: {
        ...periodAnalysis,
        beginningRows: periodAnalysis.beginning.rows.map(buildResolvedPositionRowFromAnalytics),
        comparisonRows: comparisonRows.map(buildResolvedPositionRowFromAnalytics),
        endingRows: periodPositionRows,
      },
    };
  }

  const snapshot = buildPortfolioSnapshot({
    asOfDate: effectiveAsOfDate,
    calculationAsOfDate: addDaysIso(effectiveAsOfDate, 1),
    portfolios: portfolioList,
    funds: fundList,
    positions: openPositions,
    activities: activityList,
    valuations: positionValuationList,
    securities: securityList,
    securityValuations: securityValuationList,
  });

  return {
    portfolios: portfolioList,
    funds: fundList,
    positions: openPositions,
    rows: snapshot.rows
      .map(buildResolvedPositionRowFromAnalytics)
      .filter(shouldIncludePositionRow),
    periodAnalysis: null,
  };
}
